// React Query hooks for the WB VAT rates surface (Phase C5).
//
// Pairs with /api/v1/wb/vat-rates/* — list (non-paginated, merged
// globals + tenant rows) / create / patch / delete. DTOs mirror
// server/src/modules/wb/vat-rates/types.ts (see
// WEIGHBRIDGE_SERVER_CONTRACT.md §C5).
//
// percentage on the wire:
//   The server's MySQL DECIMAL column is returned via mysql2 as a
//   string. The contract documents it as a `number` because most
//   client paths coerce on parse, but defensively we parseFloat() on
//   read AND ensure outgoing payloads are JS numbers. A row whose
//   percentage round-trips through this hook is always a number on
//   the React side.
//
// Global rows (is_global=true) have `company_id IS NULL` on the server
// and are READ-ONLY. PATCH/DELETE on a global row returns 403; the UI
// gates the action buttons accordingly, but the hooks here remain
// untyped against that constraint so the 403 is surfaced if the UI
// ever lets it slip through.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types -----------------------------------------------------

export interface WbVatRate {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly percentage: number;
  readonly is_exempt: boolean;
  readonly is_default: boolean;
  readonly is_active: boolean;
  readonly is_global: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

/** Server may return percentage as a string (mysql2 DECIMAL). Normalise
 *  every list-row to a JS number before it leaves the hook. */
function normaliseRate(raw: Record<string, unknown>): WbVatRate {
  const pct = raw['percentage'];
  return {
    enc_id: raw['enc_id'] as string,
    name: raw['name'] as string,
    code: raw['code'] as string,
    percentage:
      typeof pct === 'string' ? parseFloat(pct) : (pct as number),
    is_exempt: !!raw['is_exempt'],
    is_default: !!raw['is_default'],
    is_active: !!raw['is_active'],
    is_global: !!raw['is_global'],
    created_at: raw['created_at'] as string,
    updated_at: raw['updated_at'] as string,
  };
}

export interface CreateVatRateInput {
  name: string;
  code: string;
  percentage: number;
  is_exempt?: boolean;
  is_default?: boolean;
  is_active?: boolean;
}

export interface UpdateVatRateInput {
  name?: string;
  percentage?: number;
  is_exempt?: boolean;
  is_default?: boolean;
  is_active?: boolean;
}

// -- Queries ---------------------------------------------------------------

export function useWbVatRates() {
  return useQuery<{ vat_rates: WbVatRate[] }>({
    queryKey: queryKeys.wb.vatRates,
    queryFn: async () => {
      const res = await apiClient.get<{ vat_rates: Record<string, unknown>[] }>(
        endpoints.wb.vatRates,
      );
      return { vat_rates: res.data.vat_rates.map(normaliseRate) };
    },
    staleTime: 60_000,
  });
}

// -- Mutations -------------------------------------------------------------

export function useCreateVatRate() {
  const qc = useQueryClient();
  return useMutation<{ vat_rate: WbVatRate }, Error, CreateVatRateInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { vat_rate: Record<string, unknown> },
        CreateVatRateInput
      >(endpoints.wb.vatRates, body);
      return { vat_rate: normaliseRate(res.data.vat_rate) };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vatRates });
    },
  });
}

export function useUpdateVatRate(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ vat_rate: WbVatRate }, Error, UpdateVatRateInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { vat_rate: Record<string, unknown> },
        UpdateVatRateInput
      >(endpoints.wb.vatRate(encId), body);
      return { vat_rate: normaliseRate(res.data.vat_rate) };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vatRates });
    },
  });
}

export function useDeleteVatRate(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.vatRate(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vatRates });
    },
  });
}
